import React, { useState, useEffect } from 'react';
import { Sparkles, Flame, Trophy, Zap, TrendingUp, Target, Lightbulb, Brain } from 'lucide-react';

/**
 * Welcome Banner - Sleek hero with Lumo wisdom and streak integrated
 */

const LUMO_WISDOM = [
  {
    icon: Lightbulb,
    color: 'from-purple-500 to-indigo-500',
    text: "Try Labeling today: 'It seems like timing is a concern.' Then pause. Let them reveal.",
    tip: "Labeling works because people love being understood."
  },
  {
    icon: Brain,
    color: 'from-blue-500 to-cyan-500',
    text: "Your job isn't to convince. It's to help them discover if you're the right fit.",
    tip: "Detachment creates confidence. Desperation kills deals."
  },
  {
    icon: Zap,
    color: 'from-amber-500 to-orange-500',
    text: '"We need to think about it" means unclear value. Ask: "What specifically?"',
    tip: "Never let vague objections live. Get specific or lose the deal."
  },
  {
    icon: TrendingUp,
    color: 'from-emerald-500 to-teal-500',
    text: "Top performers close 27% of qualified deals. You're in the game at 20%+.",
    tip: "Log your real deals. What you measure, you improve."
  },
  {
    icon: Target,
    color: 'from-rose-500 to-pink-500',
    text: "Mirror their last 1-3 words as a question. 'The budget?' Watch them elaborate.",
    tip: "Mirroring feels awkward. Do it anyway. It works."
  },
  {
    icon: Sparkles,
    color: 'from-violet-500 to-purple-500',
    text: "The best salespeople are tactical empaths. Master the game AND genuinely care.",
    tip: "Practice doesn't make perfect. Practice makes permanent."
  },
  {
    icon: Lightbulb,
    color: 'from-indigo-500 to-blue-500',
    text: "Defuse tension first: 'You're probably thinking this is a waste of time...'",
    tip: "Name the elephant. It loses power when you say it first."
  },
  {
    icon: Zap,
    color: 'from-orange-500 to-red-500',
    text: '"That\'s expensive" → "What are you comparing us to?" Anchor to value, not cost.',
    tip: "Never defend your price. Defend their outcome."
  },
  {
    icon: Target,
    color: 'from-cyan-500 to-blue-500',
    text: "Replace 'Can you...' with 'How am I supposed to...' Trigger problem-solving mode.",
    tip: '"How" questions make them your collaborator.'
  },
  {
    icon: Brain,
    color: 'from-teal-500 to-emerald-500',
    text: "Pre-call ritual: 3 deep breaths. 'I'm here to help, not to need.' Confidence follows.",
    tip: "Your energy sets the frame. Control it or lose control."
  },
];

const WelcomeBanner = ({ userProfile, streakDays }) => {
  const [wisdom, setWisdom] = useState(null);

  useEffect(() => {
    // Pick wisdom based on day of year for consistency
    const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    const selectedWisdom = LUMO_WISDOM[dayOfYear % LUMO_WISDOM.length];
    setWisdom(selectedWisdom);
  }, []);

  if (!wisdom) return null;

  const WisdomIcon = wisdom.icon;
  const firstName = userProfile?.fullName?.split(' ')?.[0] || 'there';

  return (
    <div className="mb-8 relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 shadow-2xl">
      {/* Animated gradient orbs */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-500/20 to-transparent rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-blue-500/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      
      {/* Content */}
      <div className="relative z-10 p-8">
        {/* Top Row: Welcome + Stats */}
        <div className="flex items-start justify-between mb-6">
          {/* Welcome Message + VIP Badge */}
          <div className="flex items-start gap-3">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <h1 className="text-4xl font-bold text-white drop-shadow-lg">
                  Welcome back, {firstName}! 👋
                </h1>
                {userProfile?.subscriptionPlan === 'founder' && (
                  <div className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-bold shadow-lg">
                    <span>👑</span>
                    <span>Founder</span>
                  </div>
                )}
              </div>
              <p className="text-gray-300 text-lg">
                Ready to close more deals today?
              </p>
            </div>
          </div>

          {/* Stats Pills */}
          <div className="hidden md:flex items-center gap-3">
            {/* Streak */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-3 flex items-center gap-3 shadow-lg">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center shadow-md">
                <Flame className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-300 font-medium">Streak</p>
                <p className="text-2xl font-bold text-white">{streakDays}</p>
              </div>
            </div>

            {/* Level */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-3 flex items-center gap-3 shadow-lg">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center shadow-md">
                <Trophy className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-300 font-medium">Level</p>
                <p className="text-2xl font-bold text-white">{userProfile?.level || 1}</p>
              </div>
            </div>

            {/* XP */}
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl px-5 py-3 flex items-center gap-3 shadow-lg">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center shadow-md">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-300 font-medium">Total XP</p>
                <p className="text-2xl font-bold text-white">{userProfile?.xpPoints || 0}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Lumo Wisdom Card - Sleek & Integrated */}
        <div className="relative">
          <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 transition-all">
            <div className="flex items-start gap-4">
              {/* Icon */}
              <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${wisdom.color} p-2.5 shadow-lg flex-shrink-0`}>
                <WisdomIcon className="w-full h-full text-white" />
              </div>

              {/* Content */}
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-4 h-4 text-purple-400" />
                  <span className="text-xs font-semibold text-purple-400 uppercase tracking-wider">
                    Lumo's Daily Wisdom
                  </span>
                </div>
                <p className="text-white text-base font-medium leading-relaxed mb-2">
                  {wisdom.text}
                </p>
                <div className="flex items-start gap-2 text-gray-400 text-sm">
                  <Zap className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>{wisdom.tip}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Stats - Below Wisdom */}
        <div className="md:hidden mt-6 grid grid-cols-3 gap-3">
          {/* Streak */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 text-center">
            <Flame className="w-6 h-6 text-orange-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{streakDays}</p>
            <p className="text-xs text-gray-300">Streak</p>
          </div>

          {/* Level */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 text-center">
            <Trophy className="w-6 h-6 text-purple-400 mx-auto mb-1" />
            <p className="text-2xl font-bold text-white">{userProfile?.level || 1}</p>
            <p className="text-xs text-gray-300">Level</p>
          </div>

          {/* XP or VIP Badge */}
          <div className={`rounded-xl p-3 text-center ${userProfile?.subscriptionPlan === 'founder' ? 'bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30' : 'bg-white/10 backdrop-blur-md border border-white/20'}`}>
            {userProfile?.subscriptionPlan === 'founder' ? (
              <>
                <span className="text-2xl">👑</span>
                <p className="text-xs text-amber-300 font-bold mt-1">VIP</p>
              </>
            ) : (
              <>
                <Zap className="w-6 h-6 text-cyan-400 mx-auto mb-1" />
                <p className="text-2xl font-bold text-white">{userProfile?.xpPoints || 0}</p>
                <p className="text-xs text-gray-300">XP</p>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WelcomeBanner;
